import * as React from 'react';

import {
  cn,
  FieldGroup,
  fieldInnerLabelClasses,
  fieldPrefixSuffixClasses,
} from '@rematter/ui-web';
import {
  NumberFieldProps as AriaNumberFieldProps,
  ValidationResult,
} from 'react-aria-components';

import { getLocale } from '~/shared/utils/locale';

export type NumberFieldProps = Omit<
  AriaNumberFieldProps,
  'className' | 'value' | 'isRequired'
> & {
  obfuscated?: boolean;
  label?: React.ReactNode;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  prefix?: React.ReactNode;
  prefixClassName?: string;
  suffix?: React.ReactNode;
  suffixClassName?: string;
  align?: 'left' | 'center' | 'right';
  decimalPlaces?: number;
  minDecimalPlaces?: number;
  maxDecimalPlaces?: number;
  showSteppers?: boolean;
  seamless?: boolean;
  preFocused?: boolean;
  flat?: boolean;
  className?: string;
  selectOnFocus?: boolean;
  destructive?: boolean;
  usingVirtualKeyboard?: boolean;
  highlighted?: boolean;
  value?: number | string;
  isReadOnly?: boolean;
  formatNumber?: boolean;
  required?: boolean;
  transparent?: boolean;
  placeholder?: string;
  innerLabel?: string;
  allowNegative?: boolean;
};

// TODO: This is a duplicate of the NumberField component in @rematter/ui-web
// We need to refactor this to use the component from @rematter/ui-web
// and remove this component.
export const NumberField = React.forwardRef<HTMLDivElement, NumberFieldProps>(
  (
    {
      className,
      obfuscated,
      // label,
      // description,
      // errorMessage,
      prefix,
      prefixClassName,
      suffix,
      suffixClassName,
      align,
      // flat,
      // seamless,
      // preFocused,
      // transparent,
      decimalPlaces,
      minDecimalPlaces,
      maxDecimalPlaces,
      selectOnFocus,
      value,
      // destructive,
      usingVirtualKeyboard,
      isDisabled,
      required,
      // highlighted,
      isReadOnly,
      maxValue,
      minValue,
      placeholder,
      formatNumber: shouldFormatNumber = true,
      innerLabel,
      allowNegative,
      ...props
    },
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const disabled = obfuscated || isDisabled;
    const [isFocused, setIsFocused] = React.useState(false);

    const inputRef = React.useRef<HTMLInputElement>(null);

    const formatOptions = React.useMemo(
      () => ({
        minimumFractionDigits: obfuscated
          ? 0
          : (minDecimalPlaces ?? decimalPlaces),
        maximumFractionDigits: obfuscated
          ? 0
          : (maxDecimalPlaces ?? decimalPlaces),
      }),
      [decimalPlaces, maxDecimalPlaces, minDecimalPlaces, obfuscated],
    );

    const formattedValue = React.useMemo(() => {
      const isFormattableValue =
        value !== undefined && value !== '' && !Number.isNaN(Number(value));

      if (isFormattableValue && shouldFormatNumber) {
        const formatter = new Intl.NumberFormat(
          getLocale(),
          isFocused
            ? {
                minimumFractionDigits: 0,
                maximumFractionDigits: maxDecimalPlaces ?? 10,
              }
            : formatOptions,
        );
        const trailingPointAndZeroes = /\.0*$/.test(String(value))
          ? `.${String(value).split('.')[1]}`
          : '';

        return `${formatter.format(Number(value || '0'))}${trailingPointAndZeroes}`;
      }

      if (isFormattableValue && !shouldFormatNumber) {
        return String(value);
      }

      return '';
    }, [value, shouldFormatNumber, isFocused, maxDecimalPlaces, formatOptions]);

    const formattedValueRef = React.useRef(formattedValue);
    formattedValueRef.current = formattedValue;

    const [innerValue, setInnerValue] = React.useState(formattedValue);

    React.useEffect(() => {
      setInnerValue(formattedValueRef.current);
    }, [value]);

    const handleInnerChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ): void | undefined => {
      const decimals = e.target.value.split('.')[1] ?? '';

      if (maxDecimalPlaces && decimals.length > maxDecimalPlaces) {
        return;
      }

      const replacedValue = e.target.value.replace(/[^0-9.]/g, '');
      if (allowNegative && e.target.value.startsWith('-')) {
        setInnerValue(`-${replacedValue}`);
      } else {
        setInnerValue(replacedValue);
      }
    };

    const handleChange = () => {
      const stringToNumber = (stringValue: string) => {
        const replacedValue = stringValue.replace(/[^0-9.]/g, '');
        if (allowNegative && stringValue.startsWith('-')) {
          return Number(`-0${replacedValue}`);
        }
        return Number(`0${replacedValue}`);
      };
      const newValue = innerValue
        ? Math.max(
            minValue ?? Number.MIN_SAFE_INTEGER,
            Math.min(
              stringToNumber(innerValue),
              maxValue ?? Number.MAX_SAFE_INTEGER,
            ),
          )
        : (undefined as never);

      if (newValue !== value) {
        props.onChange?.(newValue);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      if (selectOnFocus) {
        inputRef.current?.select();
      }
      setInnerValue(
        usingVirtualKeyboard
          ? value !== undefined && value !== null
            ? String(value)
            : ''
          : formattedValueRef.current,
      );
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      handleChange();
      props.onBlur?.(e);
    };

    return (
      <div className={cn('flex flex-col gap-1', className)} ref={ref}>
        <FieldGroup
          aria-required={required}
          className={cn('group', innerLabel && 'py-0')}
          isDisabled={disabled}
          onMouseDown={() =>
            setTimeout(() => {
              if (document.activeElement !== inputRef.current) {
                inputRef.current?.focus();
              }
            })
          }
        >
          <>
            {prefix && (
              <div
                className={cn(
                  'flex items-center font-light text-inherit opacity-60',
                  fieldPrefixSuffixClasses.base,
                  prefixClassName,
                )}
              >
                {prefix}
              </div>
            )}
            <div className="flex size-full flex-col">
              {innerLabel && (
                <div
                  className={cn(
                    fieldInnerLabelClasses.base,
                    fieldInnerLabelClasses.right,
                    'rac-disabled:cursor-not-allowed',
                    'h-full min-h-0 p-0',
                  )}
                >
                  {innerLabel}
                </div>
              )}
              <div className="flex size-full items-center gap-1">
                <input
                  ref={inputRef}
                  type={obfuscated ? 'password' : 'text'}
                  className={cn(
                    'size-full min-w-0 flex-1 bg-transparent py-1 text-base text-current outline-0',
                    align === 'left' && 'text-left',
                    align === 'right' && 'text-right',
                    align === 'center' && 'text-center',
                  )}
                  placeholder={placeholder}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={disabled}
                  readOnly={isReadOnly}
                  value={isFocused ? innerValue : formattedValue}
                  onChange={handleInnerChange}
                  onKeyDown={props.onKeyDown}
                  onKeyDownCapture={(e) => {
                    if (e.key === 'ArrowRight') e.stopPropagation();
                    if (e.key === 'ArrowLeft') e.stopPropagation();
                    if (e.key === 'Enter') handleChange();
                    if (e.key === ' ') {
                      return e.preventDefault();
                    }
                  }}
                />
                {suffix && (
                  <div
                    className={cn(
                      fieldPrefixSuffixClasses.base,
                      'font-light text-current opacity-60',
                      suffixClassName,
                    )}
                  >
                    {suffix}
                  </div>
                )}
              </div>
            </div>
          </>
        </FieldGroup>
      </div>
    );
  },
);
